/**
 * Task 2 turn-taking policy — pure helpers for continuation timing and deadline suppression.
 * Owner LOCKED 2026-09-15 (Phase D-01).
 */

/** Silence required before arming a continuation check-in (unchanged). */
export const TASK2_CONTINUATION_SILENCE_MS = 10_000;

/**
 * Do not start new examiner speech when remaining time cannot fit the max contracted reply.
 * topicExtra contract: max 12 words @ ~2 spoken words/s ≈ 6s.
 */
export const TASK2_DEADLINE_SPEECH_SUPPRESSION_SECONDS = 6;

export type Task2AssistantOutputKind = 'none' | 'normal' | 'continue_prompt' | 'ending';

export type Task2ContinuationToolName = 'prompt_continue' | 'check_user_silence' | 'end_task' | 'thank_you_flow';

/** Proactive check-in uses static local continuation audio (canonical single authority). */
export const TASK2_CONTINUATION_AUTHORITY = 'static_local_audio' as const;

/** True when examiner output is a proactive continuation/check-in (not a normal answer). */
export function isProactiveContinuationActive(
  outputKind: Task2AssistantOutputKind,
  isLocalAudioActive: boolean,
): boolean {
  return outputKind === 'continue_prompt' || (isLocalAudioActive && outputKind === 'continue_prompt');
}

/**
 * Normal examiner answers block learner speech during active WebRTC audio.
 * Proactive continuation yields to the learner immediately.
 */
export function shouldDropLearnerSpeechDuringAssistantAudio(
  isAssistantAudioActive: boolean,
  outputKind: Task2AssistantOutputKind,
  isLocalAudioActive: boolean,
): boolean {
  if (!isAssistantAudioActive) return false;
  return !isProactiveContinuationActive(outputKind, isLocalAudioActive);
}

/** Hard timer stays authoritative; suppress starting new speech inside this window. */
export function shouldSuppressNewExaminerSpeech(remainingSeconds: number): boolean {
  return remainingSeconds <= TASK2_DEADLINE_SPEECH_SUPPRESSION_SECONDS;
}

/** Whether post-reply silence may arm the static continuation check-in. */
export function shouldScheduleContinuationPrompt(
  remainingSeconds: number,
  continuePromptUsedOnce: boolean,
  continuationRearmBlocked: boolean,
): boolean {
  if (continuationRearmBlocked) return false;
  if (shouldSuppressNewExaminerSpeech(remainingSeconds)) return false;
  if (remainingSeconds <= 45) return false;
  return !continuePromptUsedOnce;
}

/** Continuation tool acknowledgements must not spawn a parallel model utterance. */
export function shouldCreateResponseForToolAck(toolName: Task2ContinuationToolName): boolean {
  if (toolName === 'prompt_continue' || toolName === 'check_user_silence') return false;
  if (toolName === 'end_task') return false;
  return false;
}

/** After learner wins over proactive continuation, block re-arm until next normal reply ends. */
export function shouldClearContinuationRearmBlock(
  outputKindAtAudioEnd: Task2AssistantOutputKind,
  continuationRearmBlocked: boolean,
): boolean {
  return continuationRearmBlocked && outputKindAtAudioEnd === 'normal';
}
